import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Modal, Button } from "react-bootstrap";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import StandardDatePicker from "./StandardDatePicker";
import { getPresentExpos, getExpoById } from "../Apis/ExpoAPIs";
import {
  saveVisitorEnquiry,
  getVisitorEnquiriesByExpo,
  updateVisitorStatus,
  updateVisitor,
} from "../Apis/VisitorEnquiryAPIs";
import { uploadFile } from "../Apis/ManagerAPIs";
import { formatDate } from "../helper/DateHelper";
import { getImageUrl } from "../Apis/api";
import TablePagination from "./TablePagination";
import usePermissions from "../hook/userPermission";
import { getLocations } from "../Apis/MasterAPIs";
import { getClients } from "../Apis/ClientAPIs";

const formatIndianNumber = (val) => {
  if (!val && val !== 0) return "";
  let str = val.toString().replace(/,/g, "");
  if (isNaN(str)) return str;

  let parts = str.split('.');
  let lastThree = parts[0].substring(parts[0].length - 3);
  let otherNumbers = parts[0].substring(0, parts[0].length - 3);
  if (otherNumbers !== '') {
    lastThree = ',' + lastThree;
  }
  let res = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + lastThree;
  if (parts.length > 1) {
    res += "." + parts[1];
  }
  return res;
};

// ── Helper: dedup array by id ─────────────────────────────────────────────────
const dedupById = (arr) => {
  const seen = new Map();
  arr.forEach((item) => seen.set(item.id, item));
  return Array.from(seen.values());
};

// ── Helper: safe localStorage read with dedup ─────────────────────────────────
const readFromStorage = (key, fallback) => {
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      const parsed = JSON.parse(stored);
      return dedupById(parsed);
    }
  } catch (_) { }
  return fallback;
};

// ── Defined at MODULE LEVEL so they are never recreated on re-render ──────────
const defaultEnquiry = [
  {
    id: 102,
    name: "Alex",
    phone: "8765432109",
    company: "Global Inc",
    email: "alex@example.com",
    status: "Followup",
    source: "Website",
    paymentType: "UPI",
    bookingAmount: "1500",
    remarksHistory: [
      {
        date: "2026-05-01",
        text: "Initial enquiry, needs followup",
        status: "Interested",
      },
    ],
    nextDate: "2026-05-05",
  },
];

const defaultBooked = [
  {
    id: 201,
    name: "Hari Dev",
    phone: "9876543210",
    company: "Prasath Tech",
    email: "hari@example.com",
    status: "Booked",
    bookingAmount: "5000",
    paymentType: "Cash",
    source: "Mobile",
    screenshot: null,
    remarksHistory: [
      { date: "2026-04-30", text: "Payment confirmed", status: "Booked" },
    ],
    bookedDate: "2026-04-30",
  },
];

const VisitorReportsLayer = () => {
  const { hasPermission } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") === "booked" ? "booked" : "enquiry";
  const expoId = searchParams.get("expoId") || "";

  const [loading, setLoading] = useState(false);
  const [dynamicExpoName, setDynamicExpoName] = useState("");
  const [expoInfo, setExpoInfo] = useState(null);
  const [enquiryList, setEnquiryList] = useState([]);
  const [bookedList, setBookedList] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);


  useEffect(() => {
    if (expoId) {
      fetchVisitorData();
    } else {
      // Fallback to storage if no specific expo is selected via URL
      setEnquiryList(readFromStorage("visitor_enquiry_list", []));
      setBookedList(readFromStorage("visitor_booked_list", []));
    }
  }, [expoId]);

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value));
    setCurrentPage(0);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const fetchVisitorData = async () => {
    setLoading(true);
    try {
      const res = await getVisitorEnquiriesByExpo(expoId);
      if (res.status) {
        const data = res.response?.data || res.response || [];

        // Extract expo info from backend response
        const expoDetails = res.response;
        setExpoInfo(expoDetails);
        const name = expoDetails?.expoName || expoDetails?.name || "";
        setDynamicExpoName(name);
      }
    } catch (err) {
      console.error("Error fetching visitor data:", err);
    } finally {
      setLoading(false);
    }
  };

  const [showAddModal, setShowAddModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [currentVisitor, setCurrentVisitor] = useState(null);
  const [statusUpdateData, setStatusUpdateData] = useState({
    newStatus: "",
    remark: "",
    date: "",
    showForm: false,
  });

  const [locationOptions, setLocationOptions] = useState([]);

  useEffect(() => {
    const fetchLocations = async () => {
      const res = await getLocations();
      if (res.status) {
        const locData = res.response?.data || res.response || [];
        setLocationOptions(
          Array.isArray(locData)
            ? locData.map((l) => ({ value: l._id || l.id, label: l.name }))
            : []
        );
      }
    };
    fetchLocations();
  }, []);

  useEffect(() => {
    const openModal = searchParams.get("openModal");
    if (openModal === "true") {
      setIsEditMode(false);
      setNewVisitor({
        name: searchParams.get("name") || "",
        mobileNo: searchParams.get("phone") || searchParams.get("mobile") || "",
        companyName: searchParams.get("companyName") || "",
        email: searchParams.get("email") || "",
        city: "",
        state: "",
        remark: "",
        date: new Date().toISOString().split("T")[0],
        location: "",
      });
      setPaymentMethod(expoInfo?.entryType === "Paid" ? "UPI" : "Free");
      setPaymentStatus(expoInfo?.entryType === "Paid" ? "Paid" : "Free");
      setScreenshot(null);
      setBookingAmount("");
      setShowAddModal(true);

      // Clean search parameters to prevent reopening on subsequent renders
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("openModal");
      newParams.delete("name");
      newParams.delete("phone");
      newParams.delete("mobile");
      newParams.delete("email");
      newParams.delete("companyName");
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, expoInfo]);

  const [newVisitor, setNewVisitor] = useState({
    name: "",
    mobileNo: "",
    companyName: "",
    email: "",
    city: "",
    state: "",
    remark: "",
    date: new Date().toISOString().split("T")[0],
    location: "",
  });

  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [visitorToDeleteId, setVisitorToDeleteId] = useState(null);

  const [subFilter, setSubFilter] = useState("All");

  const fetchClientByMobile = async (mobile) => {
    try {
      const res = await getClients({ page: 0, limit: 10, mobileNumber: mobile, isActive: true });
      if (res.status) {
        const clientList = res.response?.data || res.response || [];
        if (clientList.length > 0) {
          const client = clientList[0];
          setNewVisitor((prev) => ({
            ...prev,
            name: client.keyPerson || prev.name,
            companyName: client.companyName || prev.companyName,
            email: client.email || prev.email,
            city: client.city || prev.city,
            state: client.state || prev.state,
          }));
          setFormErrors((prev) => ({
            ...prev,
            name: client.keyPerson ? null : prev.name,
            email: client.email ? null : prev.email,
          }));
          toast.success("Client details autofilled!");
        }
      }
    } catch (err) {
      console.error("Error fetching client by mobile:", err);
    }
  };

  const handleVisitorChange = (e) => {
    const { name, value } = e.target;

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrs = { ...prev };
        delete newErrs[name];
        return newErrs;
      });
    }

    if (name === "name") {
      const filteredValue = value.replace(/[^a-zA-Z\s]/g, "");
      const capitalizedValue = filteredValue.charAt(0).toUpperCase() + filteredValue.slice(1);
      setNewVisitor((prev) => ({ ...prev, [name]: capitalizedValue }));
      return;
    }
    if (name === "mobileNo") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
      setNewVisitor((prev) => ({ ...prev, [name]: digitsOnly }));
      if (digitsOnly.length === 10) {
        fetchClientByMobile(digitsOnly);
      }
      return;
    }
    setNewVisitor((prev) => ({ ...prev, [name]: value }));
  };



  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("Free");
  const [paymentStatus, setPaymentStatus] = useState("Free");
  const [screenshot, setScreenshot] = useState(null);
  const [bookingAmount, setBookingAmount] = useState("");
  const [remark, setRemark] = useState("");
  const [bookingDate, setBookingDate] = useState(new Date().toISOString().split('T')[0]);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    localStorage.setItem("visitor_enquiry_list", JSON.stringify(enquiryList));
  }, [enquiryList]);

  useEffect(() => {
    localStorage.setItem("visitor_booked_list", JSON.stringify(bookedList));
  }, [bookedList]);

  const handleTabChange = (tab) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("tab", tab);
    setSearchParams(newParams);
  };

  const handleOpenStatusModal = (visitor) => {
    setCurrentVisitor(visitor);
    setStatusUpdateData({
      newStatus: "",
      remark: "",
      date: "",
      showForm: false,
    });
    setShowStatusModal(true);
  };

  const handleSaveStatusUpdate = async () => {
    if (!statusUpdateData.remark) {
      toast.error("Please enter a remark");
      return;
    }

    if (statusUpdateData.newStatus === "Followup" && !statusUpdateData.date) {
      toast.error("Please select a next followup date");
      return;
    }

    setLoading(true);
    try {
      let payload = {
        status: statusUpdateData.newStatus,
        date: statusUpdateData.newStatus === "Followup" ? statusUpdateData.date : new Date().toISOString().split("T")[0],
        remark: statusUpdateData.remark,
      };

      const res = await updateVisitorStatus(
        currentVisitor._id || currentVisitor.id,
        payload,
      );

      if (res.status) {
        toast.success(
          statusUpdateData.newStatus === "Rejected"
            ? "Lead Marked As Rejected"
            : "Status Updated!",
        );
        fetchVisitorData();
        setShowStatusModal(false);
      }
    } catch (err) {
      console.error("Status update error:", err);
      toast.error("Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  const handleAddOrEditVisitor = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!(newVisitor.name || "").trim()) errors.name = "Name is Required";
    if (!(newVisitor.mobileNo || "").trim() || newVisitor.mobileNo.length !== 10)
      errors.mobileNo = "Valid 10-digit Phone is Required";
    if (newVisitor.email && newVisitor.email.trim()) {
      const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
      if (!emailRegex.test(newVisitor.email)) {
        errors.email = "Invalid email address (must have at least 2 chars after .)";
      }
    }
    if (!(newVisitor.date || "").trim()) {
      errors.date = "Date is Required";
    }
    if (!(newVisitor.location || "").trim()) {
      errors.location = "Location is Required";
    }
    if (!isEditMode && activeTab === "booked") {
      if (paymentStatus === "Paid") {
        if (!bookingAmount) errors.bookingAmount = "Booking amount is required";
        if (!paymentMethod) errors.paymentMethod = "Payment type is required";
        if (!remark?.trim()) errors.remark = "Remark is required for paid booking";
        if (paymentMethod === "UPI" && !screenshot) errors.screenshot = "Payment screenshot is required for UPI";
      }
    }


    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    if (isEditMode) {
      setLoading(true);
      try {
        const editPayload = {
          name: newVisitor.name,
          mobileNo: newVisitor.mobileNo,
          email: newVisitor.email,
          companyName: newVisitor.companyName,
          city: newVisitor.city,
          state: newVisitor.state,
          remark: newVisitor.remark,
          registerDate: newVisitor.date,
          location: newVisitor.location,
        };
        const res = await updateVisitor(currentVisitor._id || currentVisitor.id, editPayload);
        if (res.status) {
          toast.success("Visitor Updated Successfully");
          fetchVisitorData();
          setShowAddModal(false);
          setIsEditMode(false);
        }
      } catch (err) {
        console.error("Update error:", err);
        toast.error("Failed To Update Visitor");
      } finally {
        setLoading(false);
      }
    } else {
      const visitorPayload = {
        expoId: expoId,
        name: newVisitor.name,
        mobileNo: newVisitor.mobileNo,
        email: newVisitor.email,
        companyName: newVisitor.companyName,
        city: newVisitor.city,
        state: newVisitor.state,
        remark: activeTab === "booked" ? remark : newVisitor.remark,
        registerDate: newVisitor.date,
        location: newVisitor.location,
        status: activeTab === "booked" ? "Booked" : "Enquiry",
      };

      if (activeTab === "booked") {
        visitorPayload.paymentStatus = paymentStatus;
        visitorPayload.paymentMethod = paymentStatus === "Free" ? "Free" : (paymentStatus === "Pending" ? "Pending" : paymentMethod);
        visitorPayload.amount = (paymentStatus === "Free" || paymentStatus === "Pending") ? 0 : Number(bookingAmount || 0);
        visitorPayload.paymentScreenshot = (paymentStatus === "Free" || paymentStatus === "Pending") ? null : screenshot;
      }

      setLoading(true);
      try {
        const res = await saveVisitorEnquiry(visitorPayload);
        if (res.status) {
          toast.success(activeTab === "enquiry" ? "Enquiry Added Successfully" : "Booking Added Successfully");
          fetchVisitorData();
          setShowAddModal(false);
          setIsEditMode(false);
          setNewVisitor({
            name: "",
            mobileNo: "",
            companyName: "",
            email: "",
            city: "",
            state: "",
            remark: "",
            date: new Date().toISOString().split("T")[0],
            location: "",
          });
          setRemark("");
          setScreenshot(null);
          setBookingAmount("");
        }
      } catch (err) {
        console.error("Save error:", err);
        toast.error("Failed To Save Enquiry");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleScreenShotUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      formDataUpload.append("folder", "payments");

      setLoading(true);
      try {
        const res = await uploadFile(formDataUpload);
        if (res.status) {
          const filePath = res.response?.data?.path || res.response;
          setScreenshot(filePath);
          toast.success("Screenshot Uploaded Successfully");
        } else {
          toast.error("Upload Failed");
        }
      } catch (err) {
        console.error("Upload error:", err);
        toast.error("Error Uploading File");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditClick = (visitor) => {
    setCurrentVisitor(visitor);

    // Find the ID of the location if it is a string/name
    let locVal = "";
    if (visitor.location) {
      if (typeof visitor.location === 'object') {
        locVal = visitor.location?._id || visitor.location?.id || "";
      } else {
        // It's a string, might be name or ID
        const found = locationOptions.find(o => o.value === visitor.location || o.label === visitor.location);
        locVal = found ? found.value : visitor.location;
      }
    }

    setNewVisitor({
      name: visitor.name || "",
      mobileNo: visitor.mobileNo || "",
      companyName: visitor.companyName || visitor.company || "",
      email: visitor.email || "",
      city: visitor.city || "",
      state: visitor.state || "",
      remark: visitor.remark || "",
      date: visitor.registerDate ? visitor.registerDate.split("T")[0] : (visitor.date ? visitor.date.split("T")[0] : (visitor.createdAt ? visitor.createdAt.split("T")[0] : new Date().toISOString().split("T")[0])),
      location: locVal,
    });
    setIsEditMode(true);
    setShowAddModal(true);
  };

  const handleDeleteVisitor = (id) => {
    setVisitorToDeleteId(id);
    setShowDeleteConfirmModal(true);
  };

  const confirmDeleteVisitor = () => {
    if (visitorToDeleteId) {
      const updateFn = activeTab === "enquiry" ? setEnquiryList : setBookedList;
      updateFn((prev) => prev.filter((v) => v.id !== visitorToDeleteId));
      toast.error("Visitor Deleted Successfully");
      setShowDeleteConfirmModal(false);
      setVisitorToDeleteId(null);
    }
  };

  const moveVisitorToBooked = (item) => {
    setSelectedVisitor(item);
    // Auto-detect payment status from expo's entryType
    const entryType = item?.expoId?.entryType || "";
    let initialPaymentStatus = "Pending";
    if (entryType === "Free") {
      initialPaymentStatus = "Free";
    } else if (entryType === "Paid") {
      initialPaymentStatus = "Paid";
    } else {
      // Fallback from item's own payment info
      const isFree = item.paymentStatus === "Free" || item.paymentMethod === "Free" || item.bookingAmount === 0 || item.bookingAmount === "0";
      initialPaymentStatus = isFree ? "Free" : "Pending";
    }
    setPaymentStatus(initialPaymentStatus);
    setPaymentMethod(initialPaymentStatus === "Free" ? "Free" : initialPaymentStatus === "Pending" ? "Pending" : (item.paymentMethod || "Cash"));
    setBookingAmount(initialPaymentStatus === "Free" ? "0" : (item.bookingAmount || ""));
    setScreenshot(item.screenshot || null);
    setRemark(item.remark || "");
    setShowBookingModal(true);
  };

  const handleBookingConfirm = async () => {
    if (paymentStatus === "Paid") {
      if (!bookingAmount) {
        toast.error("Booking amount is required");
        return;
      }
      if (!paymentMethod) {
        toast.error("Payment type is required");
        return;
      }
      if (!remark?.trim()) {
        toast.error("Remark is required for paid booking");
        return;
      }
      if (paymentMethod === "UPI" && !screenshot) {
        toast.error("Payment screenshot is required for UPI");
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        status: "Booked",
        date: bookingDate,
        paymentStatus: paymentStatus,
        paymentScreenshot: (paymentStatus === "Free" || paymentStatus === "Pending") ? null : screenshot,
        remark: remark,
        paymentMethod: paymentStatus === "Free" ? "Free" : (paymentStatus === "Pending" ? "Pending" : paymentMethod),
        amount: (paymentStatus === "Free" || paymentStatus === "Pending") ? "0" : bookingAmount,
      };

      const res = await updateVisitorStatus(
        selectedVisitor._id || selectedVisitor.id,
        payload,
      );

      if (res.status) {
        toast.success("Converted To Booked!");
        fetchVisitorData();
        setShowBookingModal(false);
        setRemark("");
        setScreenshot(null);
      }
    } catch (err) {
      console.error("Booking conversion error:", err);
      toast.error("Failed to confirm booking");
    } finally {
      setLoading(false);
    }
  };

  const currentList = activeTab === "enquiry" ? enquiryList : bookedList;

  const filteredList = currentList.filter(item => {
    if (subFilter === "All") return true;
    if (subFilter === "Paid") {
      return (
        item.paymentStatus === "Paid" ||
        item.paymentStatus === "Partially" ||
        item.paymentStatus === "Partial" ||
        item.paymentStatus === "Partially Paid"
      );
    }
    if (subFilter === "Free") return item.paymentStatus === "Free";
    return true;
  });

  const totalPages = Math.ceil(filteredList.length / rowsPerPage) || 1;

  useEffect(() => {
    if (currentPage >= totalPages) {
      setCurrentPage(Math.max(0, totalPages - 1));
    }
  }, [filteredList.length, rowsPerPage, totalPages, currentPage]);

  const paginatedList = filteredList.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage,
  );

  return (
    <>
      <div className="card h-100 p-0 radius-12 border-0 shadow-sm">
        <div
          className="card-header border-bottom py-16 px-24 d-flex align-items-center justify-content-between flex-wrap gap-3"
          style={{
            background:
              "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
          }}
        >
          <div className="d-flex align-items-center gap-3 flex-grow-1">
            <Link
              to="/present-expo"
              className="btn btn-sm btn-light d-flex align-items-center justify-content-center w-36-px h-36-px rounded-circle p-0"
            >
              <Icon
                icon="solar:arrow-left-linear"
                className="text-xl text-dark"
              />
            </Link>
            <div className="d-flex flex-column">
              <h6 className="text-white mb-0 fw-bold d-flex align-items-center gap-2">
                <span className="text-uppercase">{dynamicExpoName || "Exhibition"}</span>
                <span className="opacity-50 fw-light">|</span>
                <span className="text-sm fw-medium">Visitor {activeTab === "enquiry" ? "Enquiry" : "Booked"} List</span>
              </h6>
              {expoInfo?.venue && (
                <div className="d-flex align-items-center gap-2 text-white opacity-75" style={{ fontSize: '11px' }}>
                  <Icon icon="solar:map-point-bold" className="text-xs" />
                  <span>{expoInfo.venue}</span>
                </div>
              )}
            </div>
          </div>
          {hasPermission("visitor_reports", "add") && (
            <button
              className="btn btn-sm fw-bold px-16 py-8 radius-8 d-flex align-items-center gap-2"
              style={{ backgroundColor: "#f7e7da", color: "black", fontWeight: "600" }}
              onClick={() => {
                setIsEditMode(false);
                setNewVisitor({
                  name: "",
                  mobileNo: "",
                  companyName: "",
                  email: "",
                  city: "",
                  state: "",
                  remark: "",
                  date: new Date().toISOString().split("T")[0],
                  location: "",
                });
                setPaymentMethod(expoInfo?.entryType === "Paid" ? "UPI" : "Free");
                setPaymentStatus(expoInfo?.entryType === "Paid" ? "Paid" : "Free");
                setScreenshot(null);
                setBookingAmount("");
                setRemark("");
                setFormErrors({});
                setShowAddModal(true);
              }}
            >
              <Icon icon="ic:baseline-plus" className="text-xl" /> {activeTab === "enquiry" ? "Add Visitor Enquiry" : "Add Booked Visitor"}
            </button>
          )}
        </div>

        <div className="card-body p-0">
          <div className="d-flex w-100">
            {[
              { id: "enquiry", label: "Visitor Enquiry", count: enquiryList.length, icon: "solar:user-bold" },
              { id: "booked", label: "Visitor Booked", count: bookedList.length, icon: "solar:users-group-two-rounded-bold" },
            ].map((tab) => (
              <button
                key={tab.id}
                className={`w-100 py-20 fw-bold d-flex align-items-center justify-content-center gap-2 transition-all ${activeTab === tab.id ? "text-danger-600 bg-white" : "text-secondary-light bg-neutral-50"}`}
                onClick={() => {
                  handleTabChange(tab.id);
                  setSubFilter("All");
                  setCurrentPage(0);
                }}
                style={{
                  border: activeTab === tab.id ? "1px solid #ed1c24" : "1px solid #e9ecef",
                  borderTop: activeTab === tab.id ? "4px solid #ed1c24" : "1px solid #e9ecef",
                  borderBottom: activeTab === tab.id ? "0" : "1px solid #e9ecef",
                  zIndex: activeTab === tab.id ? 2 : 1
                }}
              >
                <Icon icon={tab.icon} className="text-lg" />
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>

          <div className="p-24">
            <div className="table-responsive overflow-x-auto">
              <table className="table bordered-table sm-table mb-0 text-nowrap" style={{ minWidth: '1850px' }}>
                <thead
                  style={{
                    background: "linear-gradient(135deg, #bb0007, #ed1c24)",
                  }}
                >
                  <tr>
                    <th style={{ color: "white", width: '60px', whiteSpace: 'nowrap' }} className="px-16">S.No</th>
                    <th style={{ color: "white", width: '120px', whiteSpace: 'nowrap' }}>Date</th>
                    <th style={{ color: "white", width: '180px', whiteSpace: 'nowrap' }}>Name</th>
                    <th style={{ color: "white", width: '130px', whiteSpace: 'nowrap' }}>Phone</th>
                    <th style={{ color: "white", width: '220px', whiteSpace: 'nowrap' }}>Email</th>
                    <th style={{ color: "white", width: '180px', whiteSpace: 'nowrap' }}>Company</th>
                    <th style={{ color: "white", width: '150px', whiteSpace: 'nowrap' }}>Location</th>
                    <th style={{ color: "white", width: '120px', whiteSpace: 'nowrap' }}>Source</th>
                    <th style={{ color: "white", width: '180px', whiteSpace: 'nowrap' }}>Entry Type</th>
                    {expoInfo?.entryType !== "Free" && (
                      <>
                        <th style={{ color: "white", width: '180px', whiteSpace: 'nowrap' }}>Payment Status</th>
                        <th style={{ color: "white", width: '100px', whiteSpace: 'nowrap' }}>Amount</th>
                        <th style={{ color: "white", width: '220px', whiteSpace: 'nowrap' }}>Payment Image</th>
                      </>
                    )}
                    <th style={{ color: "white", width: '150px', whiteSpace: 'nowrap' }}>Status</th>
                    <th style={{ color: "white", textAlign: "center", width: '150px', whiteSpace: 'nowrap' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="12" className="text-center py-48">
                        <div className="d-flex flex-column align-items-center gap-2">
                          <Icon icon="line-md:loading-twotone-loop" className="text-4xl text-danger" />
                          <span className="text-secondary-light fw-medium">Loading visitors...</span>
                        </div>
                      </td>
                    </tr>
                  ) : paginatedList.length > 0 ? (
                    paginatedList.map((item, index) => (
                      <tr key={item.id} className={item.status === "Rejected" ? "bg-neutral-50 opacity-75" : ""}>
                        <td>{currentPage * rowsPerPage + index + 1}</td>
                        <td className="text-secondary-light">{formatDate(item.registerDate || item.date || item.createdAt || new Date())}</td>
                        <td className="fw-medium">{item.name}</td>
                        <td>{item.mobileNo}</td>
                        <td>{item.email || "N/A"}</td>
                        <td>{item.company || "N/A"}</td>
                        <td>
                          <span
                            className={`badge px-12 py-4 radius-pill ${item.source === "Mobile" ? "bg-purple-focus text-purple-main" :
                                item.source === "Website" ? "bg-warning-focus text-warning-main" :
                                  "bg-info-focus text-info-main"
                              }`}
                          >
                            {item.source || "Admin"}
                          </span>
                        </td>
                        <td>{item.paymentStatus || "-"}</td>
                        <td className="fw-bold">₹{Number(item.bookingAmount || 0).toLocaleString('en-IN')}</td>
                        <td>
                          {(item.paymentScreenshot || item.screenshot) ? (
                            <img
                              src={getImageUrl(item.paymentScreenshot || item.screenshot)}
                              alt="SS"
                              className="radius-4 cursor-pointer"
                              style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                              onClick={() => window.open(getImageUrl(item.paymentScreenshot || item.screenshot), '_blank')}
                            />
                          ) : (
                            <span className="text-secondary-light">-</span>
                          )}
                        </td>
                        <td>
                          <span
                            className={`badge px-16 py-8 radius-8 fw-bold text-white cursor-pointer hover-scale-sm d-inline-block min-w-100-px text-center ${item.status === "Booked" ? "bg-success" :
                                item.status === "Rejected" ? "bg-danger" :
                                  item.status === "Followup" ? "bg-info" :
                                    "bg-danger"
                              }`}
                            onClick={() => handleOpenStatusModal(item)}
                            style={{ fontSize: '11px', letterSpacing: '0.5px' }}
                          >
                            {item.status === "Interested" ? "ENQUIRY" :
                              item.status === "Followup" ? "FOLLOWUP" :
                                item.status.toUpperCase()}
                          </span>
                          {item.status === "Followup" && (item.nextFollowupDate || item.nextDate || item.date) && (
                            <div className="text-xs text-secondary-light mt-1 fw-bold text-center">
                              {formatDate(item.nextFollowupDate || item.nextDate || item.date)}
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="d-flex align-items-center justify-content-center gap-2">
                            <button
                              className="btn btn-sm btn-outline-primary radius-8 d-flex align-items-center gap-2 px-12"
                              onClick={() => handleOpenStatusModal(item)}
                              title="Manage Status"
                            >
                              <Icon icon="solar:calendar-add-linear" className="text-lg" />
                            </button>
                            {item.status !== "Rejected" && (
                              <button
                                className="btn btn-sm btn-outline-danger radius-8 d-flex align-items-center gap-2 px-12"
                                onClick={() => {
                                  setCurrentVisitor(item);
                                  setStatusUpdateData({
                                    newStatus: "Rejected",
                                    remark: "",
                                    date: "",
                                    showForm: true,
                                  });
                                  setShowStatusModal(true);
                                }}
                                title="Reject Lead"
                              >
                                <Icon icon="solar:close-circle-bold" className="text-lg" />
                              </button>
                            )}
                            {activeTab !== "booked" && (
                              <>
                                <button
                                  onClick={() => handleEditClick(item)}
                                  title="Edit"
                                  className="btn-edit-custom fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                                >
                                  <Icon icon="lucide:edit" className="menu-icon" />
                                </button>
                                <button
                                  onClick={() => handleDeleteVisitor(item._id || item.id)}
                                  title="Delete"
                                  className="bg-danger-focus text-danger-600 bg-hover-danger-200 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                                >
                                  <Icon icon="lucide:trash-2" className="text-xl" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={expoInfo?.entryType === "Free" ? 11 : 14} className="text-center py-40">
                        No records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-24 pb-24">
              <TablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                totalRecords={filteredList.length}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={showStatusModal}
        onHide={() => setShowStatusModal(false)}
        centered
        size="lg"
      >
        <Modal.Header
          closeButton
          style={{
            background: "linear-gradient(135deg, #000000, #bb0007, #ff0000)",
          }}
          className="text-white"
        >
          <Modal.Title className="text-sm">
            Manage Status: {currentVisitor?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div className="p-24 overflow-auto" style={{ maxHeight: "75vh" }}>
            {/* Interaction History */}
            <div className="mb-24">
              <div className="d-flex justify-content-between align-items-center mb-16">
                <h6 className="mb-0 fw-bold" style={{ fontSize: "15px" }}>Interaction History</h6>
                <span
                  className="badge fw-bold"
                  style={{
                    fontSize: "10px",
                    padding: "10px",
                    letterSpacing: "0.5px",
                    backgroundColor:
                      currentVisitor?.status === "Booked" ? "#dcfce7" :
                        currentVisitor?.status === "Rejected" ? "#fee2e2" :
                          currentVisitor?.status === "Followup" ? "#dbeafe" : "#fef3c7",
                    color:
                      currentVisitor?.status === "Booked" ? "#15803d" :
                        currentVisitor?.status === "Rejected" ? "#b91c1c" :
                          currentVisitor?.status === "Followup" ? "#1d4ed8" : "#92400e",
                    border: "none",
                    borderRadius: "6px",
                  }}
                >
                  CURRENT STATUS:{" "}
                  {currentVisitor?.status === "Interested" ? "ENQUIRY" : currentVisitor?.status?.toUpperCase()}
                </span>
              </div>

              {/* Timeline */}
              <div
                className="p-16 radius-12 border"
                style={{ backgroundColor: "#f9fafb", borderColor: "#e5e7eb" }}
              >
                {(() => {
                  const history = currentVisitor?.statusUpdateDetails || currentVisitor?.update || currentVisitor?.remarksHistory || [];
                  const displayHistory = [...history];

                  if (displayHistory.length === 0 && currentVisitor?.remark) {
                    displayHistory.push({
                      status: "Interested",
                      remark: currentVisitor.remark,
                      date: currentVisitor.createdAt || new Date(),
                    });
                  }

                  if (displayHistory.length > 0) {
                    return displayHistory
                      .slice()
                      .reverse()
                      .map((remark, idx) => {
                        const isBooked = remark.status === "Booked";
                        const dotColor = idx === 0 ? "#ed1c24" : isBooked ? "#16a34a" : "#3b82f6";
                        const borderColor = isBooked ? "#bbf7d0" : idx === 0 ? "#fecaca" : "#bfdbfe";
                        const bgColor = isBooked ? "#f0fdf4" : idx === 0 ? "#fff" : "#f0f9ff";

                        return (
                          <div key={idx} className="mb-16 position-relative">
                            {/* Dot + Label row */}
                            <div className="d-flex align-items-start gap-12">
                              <div
                                className="flex-shrink-0 mt-2"
                                style={{
                                  width: "12px",
                                  height: "12px",
                                  borderRadius: "50%",
                                  backgroundColor: dotColor,
                                  border: "2px solid white",
                                  boxShadow: `0 0 0 2px ${dotColor}44`,
                                  marginTop: "3px",
                                }}
                              />
                              <div className="flex-grow-1">
                                <div className="d-flex justify-content-between align-items-start mb-6">
                                  <span className="fw-bold text-dark" style={{ fontSize: "13px" }}>
                                    {remark.status === "Interested" ? "Initial Enquiry" :
                                      remark.status === "Followup" ? "Followup Added" :
                                        remark.status === "Booked" ? "Booking Confirmed" :
                                          remark.status === "Rejected" ? "Lead Rejected" :
                                            `Status: ${remark.status}`}
                                  </span>
                                  <span className="text-secondary-light" style={{ fontSize: "11px" }}>
                                    {formatDate(remark.createdAt || remark.actionDate || remark.date || new Date())}
                                  </span>
                                </div>

                                {/* Followup next date */}
                                {remark.status === "Followup" && remark.date && (
                                  <div
                                    className="text-xs fw-bold mb-6"
                                    style={{ color: "#1d4ed8", fontSize: "11px" }}
                                  >
                                    NEXT FOLLOWUP: {formatDate(remark.date)}
                                  </div>
                                )}

                                {/* Content card */}
                                <div
                                  className="p-10 radius-8 border"
                                  style={{ backgroundColor: bgColor, borderColor, fontSize: "13px", color: "#374151" }}
                                >
                                  {remark.remark || remark.text || (isBooked ? "Booking confirmed successfully." : "No remark provided.")}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      });
                  }

                  return (
                    <div className="text-center py-20">
                      <p className="text-secondary-light small mb-0">No previous interactions found.</p>
                    </div>
                  );
                })()}
              </div>
            </div>

            <hr className="my-16 opacity-10" />

            <div className="update-status-section">
              {statusUpdateData.showForm ? (
                <div className="bg-light p-20 radius-12 border shadow-sm">
                  <div className="d-flex justify-content-between align-items-center mb-16 pb-8 border-bottom">
                    <span className="fw-bold text-dark-main">
                      Updating to:{" "}
                      <span
                        className={
                          statusUpdateData.newStatus === "Rejected"
                            ? "text-danger"
                            : "text-primary"
                        }
                      >
                        {statusUpdateData.newStatus.toUpperCase()}
                      </span>
                    </span>
                    <button
                      className="btn btn-sm btn-link text-secondary text-decoration-none fw-bold"
                      onClick={() =>
                        setStatusUpdateData({
                          ...statusUpdateData,
                          showForm: false,
                          newStatus: "",
                        })
                      }
                    >
                      Change Action
                    </button>
                  </div>
                  <div className="row gy-3">
                    {statusUpdateData.newStatus === "Followup" && (
                      <div className="col-12 text-start">
                        <label className="form-label fw-bold small">
                          Next Followup Date <span className="text-danger">*</span>
                        </label>
                        <StandardDatePicker
                          value={statusUpdateData.date}
                          minDate="today"
                          onChange={(e) =>
                            setStatusUpdateData({
                              ...statusUpdateData,
                              date: e.target.value,
                            })
                          }
                        />
                      </div>
                    )}
                    <div className="col-12 text-start">
                      <label className="form-label fw-bold small">
                        Remark / Note *
                      </label>
                      <textarea
                        className="form-control radius-8"
                        rows="3"
                        placeholder="Enter update details..."
                        value={statusUpdateData.remark}
                        onChange={(e) =>
                          setStatusUpdateData({
                            ...statusUpdateData,
                            remark: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="col-12 d-flex gap-2 justify-content-end mt-8">
                      <button
                        className="btn btn-sm btn-light px-20 radius-8 fw-bold"
                        onClick={() =>
                          setStatusUpdateData({
                            ...statusUpdateData,
                            showForm: false,
                            newStatus: "",
                          })
                        }
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-sm btn-primary px-24 radius-8 fw-bold"
                        style={{
                          backgroundColor:
                            statusUpdateData.newStatus === "Rejected"
                              ? "#dc3545"
                              : "#0d6efd",
                        }}
                        onClick={handleSaveStatusUpdate}
                      >
                        Save Changes
                      </button>
                    </div>
                  </div>
                </div>
              ) : currentVisitor?.status === "Booked" ? (
                <div className="d-flex flex-column gap-3">
                  <div
                    className="d-flex align-items-start gap-16 p-20 radius-12 border"
                    style={{ backgroundColor: "#f0fdf4", borderColor: "#bbf7d0" }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center radius-circle flex-shrink-0"
                      style={{
                        width: "44px",
                        height: "44px",
                        backgroundColor: "#dcfce7",
                        borderRadius: "50%",
                      }}
                    >
                      <Icon icon="solar:ticket-bold" className="text-xl" style={{ color: "#16a34a" }} />
                    </div>
                    <div className="flex-grow-1">
                      <h6 className="mb-4 fw-bold" style={{ color: "#15803d", fontSize: "14px" }}>
                        Booking Confirmed
                      </h6>
                      <p className="mb-8 text-secondary-light" style={{ fontSize: "13px" }}>
                        This visitor has been booked.
                      </p>

                      <div className="d-flex flex-wrap gap-24 mt-12 pt-12 border-top" style={{ borderColor: "rgba(22, 163, 74, 0.15)" }}>
                        <div>
                          <span className="text-secondary-light d-block mb-2" style={{ fontSize: "11px" }}>Booking Date</span>
                          <strong className="text-dark" style={{ fontSize: "13px" }}>
                            {formatDate(currentVisitor?.date || currentVisitor?.registerDate || currentVisitor?.createdAt || new Date())}
                          </strong>
                        </div>
                        <div>
                          <span className="text-secondary-light d-block mb-2" style={{ fontSize: "11px" }}>Payment Method</span>
                          <strong className="text-dark" style={{ fontSize: "13px" }}>
                            {currentVisitor?.paymentMethod || "–"}
                          </strong>
                        </div>
                        {currentVisitor?.expoId?.entryType !== "Free" && (
                          <div>
                            <span className="text-secondary-light d-block mb-2" style={{ fontSize: "11px" }}>Total Contract</span>
                            <strong className="text-dark" style={{ fontSize: "13px" }}>
                              ₹{Number(currentVisitor?.amount || 0).toLocaleString("en-IN")}
                            </strong>
                          </div>
                        )}
                        <div>
                          <span className="text-secondary-light d-block mb-2" style={{ fontSize: "11px" }}>Payment Status</span>
                          <div>
                            <span
                              className="badge px-12 py-6 radius-pill fw-bold text-white"
                              style={{
                                fontSize: "11px",
                                backgroundColor:
                                  currentVisitor?.paymentStatus === "Paid" ||
                                    currentVisitor?.paymentStatus === "Partially" ||
                                    currentVisitor?.paymentStatus === "Partial"
                                    ? "#16a34a"
                                    : currentVisitor?.paymentStatus === "Pending"
                                      ? "#d97706"
                                      : "#0d6efd",
                              }}
                            >
                              {currentVisitor?.paymentStatus || "N/A"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex justify-content-end">
                    <button
                      className="btn btn-outline-danger d-flex align-items-center gap-2 px-20 py-10 radius-8 fw-bold"
                      onClick={() =>
                        setStatusUpdateData({
                          ...statusUpdateData,
                          newStatus: "Rejected",
                          showForm: true,
                        })
                      }
                    >
                      <Icon icon="solar:close-circle-bold" className="text-xl" />
                      Reject Lead
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <h6 className="mb-16 fw-bold text-dark-main" style={{ fontSize: "14px" }}>
                    Update Lead Status
                  </h6>
                  <div className="d-flex gap-3 flex-wrap">
                    <button
                      className="btn btn-outline-primary d-flex align-items-center gap-2 px-20 py-10 radius-8 fw-bold"
                      onClick={() =>
                        setStatusUpdateData({
                          ...statusUpdateData,
                          newStatus: "Followup",
                          showForm: true,
                        })
                      }
                    >
                      <Icon icon="solar:calendar-add-bold" className="text-xl" />
                      Add Followup
                    </button>
                    <button
                      className="btn btn-outline-success d-flex align-items-center gap-2 px-20 py-10 radius-8 fw-bold"
                      onClick={() => {
                        moveVisitorToBooked(currentVisitor);
                        setShowStatusModal(false);
                      }}
                    >
                      <Icon icon="solar:ticket-bold" className="text-xl" />
                      Convert to Booking
                    </button>
                    <button
                      className="btn btn-outline-danger d-flex align-items-center gap-2 px-20 py-10 radius-8 fw-bold"
                      onClick={() =>
                        setStatusUpdateData({
                          ...statusUpdateData,
                          newStatus: "Rejected",
                          showForm: true,
                        })
                      }
                    >
                      <Icon icon="solar:close-circle-bold" className="text-xl" />
                      Reject Lead
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="bg-light border-top-0 d-flex justify-content-between align-items-center px-24 py-16">
          <div
            className="d-flex align-items-center gap-2 text-secondary-light"
            style={{ fontSize: "13px" }}
          >
            <Icon icon="solar:info-circle-bold" className="text-lg" />
            <span>History is based on current lead status.</span>
          </div>
          <Button
            variant="secondary"
            className="px-24 py-10 radius-8 fw-bold"
            style={{ backgroundColor: "#6c757d" }}
            onClick={() => setShowStatusModal(false)}
          >
            Close Popup
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteConfirmModal}
        onHide={() => setShowDeleteConfirmModal(false)}
        centered
      >
        <Modal.Body className="text-center p-5">
          <div className="d-flex justify-content-center mb-3">
            <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px">
              <Icon
                icon="lucide:trash-2"
                className="text-danger-600 text-xxl"
              />
            </div>
          </div>
          <h6>Are you sure you want to delete this visitor?</h6>
          <p className="text-secondary-light mb-4">
            This action cannot be undone.
          </p>
          <div className="d-flex justify-content-center gap-3 mt-4">
            <button
              type="button"
              className="btn btn-outline-secondary px-32 radius-8"
              onClick={() => setShowDeleteConfirmModal(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn px-32 radius-8"
              onClick={confirmDeleteVisitor}
              style={{
                backgroundColor: "#ed1c24",
                color: "white",
                borderColor: "#ed1c24",
              }}
            >
              Confirm
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        centered
        size="lg"
      >
        <Modal.Header
          closeButton
          style={{
            background: "linear-gradient(135deg, #000000, #bb0007, #ff0000)",
          }}
          className="text-white"
        >
          <Modal.Title className="text-sm">
            {isEditMode ? "EDIT" : "ADD"} {activeTab.toUpperCase()}
          </Modal.Title>
        </Modal.Header>
        <form onSubmit={handleAddOrEditVisitor}>
          <Modal.Body className="p-24">
            <div className="row gy-3 text-start">
              <div className="col-md-6">
                <label className="form-label fw-bold">Phone <span className="text-danger">*</span></label>
                <input
                  type="text"
                  name="mobileNo"
                  className={`form-control ${formErrors.mobileNo ? "is-invalid" : ""}`}
                  placeholder="Enter 10-digit Phone"
                  value={newVisitor.mobileNo || ""}
                  onChange={handleVisitorChange}
                />
                {formErrors.mobileNo && (
                  <div className="invalid-feedback">{formErrors.mobileNo}</div>
                )}
              </div>
              <div className="col-md-6">
                <label className="form-label fw-bold">Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  name="name"
                  className={`form-control ${formErrors.name ? "is-invalid" : ""}`}
                  placeholder="Enter Name"
                  value={newVisitor.name}
                  onChange={handleVisitorChange}
                />
                {formErrors.name && (
                  <div className="invalid-feedback">{formErrors.name}</div>
                )}
              </div>
              <div className="col-md-6">
                <label className="form-label fw-bold">Email</label>
                <input
                  type="email"
                  name="email"
                  className={`form-control ${formErrors.email ? "is-invalid" : ""}`}
                  placeholder="Enter Email Address"
                  value={newVisitor.email}
                  onChange={handleVisitorChange}
                />
                {formErrors.email && (
                  <div className="invalid-feedback">{formErrors.email}</div>
                )}
              </div>
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  Company Name (Optional)
                </label>
                <input
                  type="text"
                  name="companyName"
                  className="form-control"
                  placeholder="Enter Company Name"
                  value={newVisitor.companyName}
                  onChange={handleVisitorChange}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-bold">Date <span className="text-danger">*</span></label>
                <StandardDatePicker
                  name="date"
                  value={newVisitor.date}
                  maxDate="today"
                  onChange={handleVisitorChange}
                />
                {formErrors.date && (
                  <div className="text-danger text-sm mt-1">{formErrors.date}</div>
                )}
              </div>
              <div className="col-md-6">
                <label className="form-label fw-bold">Location <span className="text-danger">*</span></label>
                <Select
                  options={locationOptions}
                  isSearchable
                  value={locationOptions.find(o => o.value === newVisitor.location) || null}
                  onChange={(val) => {
                    setNewVisitor(prev => ({ ...prev, location: val ? val.value : "" }));
                    if (formErrors.location) {
                      setFormErrors(prev => {
                        const newErrs = { ...prev };
                        delete newErrs.location;
                        return newErrs;
                      });
                    }
                  }}
                  styles={selectStyles({ error: !!formErrors.location, maxHeight: "200px" })}
                  placeholder="Select Location"
                />
                {formErrors.location && (
                  <div className="text-danger text-sm mt-1">{formErrors.location}</div>
                )}
              </div>
              {activeTab === "booked" && expoInfo?.entryType !== "Free" && (
                <>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Payment Status *</label>
                    <select
                      className="form-select radius-8"
                      value={paymentStatus}
                      onChange={(e) => {
                        const val = e.target.value;
                        setPaymentStatus(val);
                        if (val === "Pending" || val === "Free") {
                          setBookingAmount("0");
                          setPaymentMethod(val);
                        } else {
                          setBookingAmount("");
                          setPaymentMethod("Cash");
                        }
                      }}
                    >
                      <option value="Paid">Paid</option>
                      <option value="Pending">Pending</option>
                    </select>
                  </div>
                  {paymentStatus === "Paid" && (
                    <>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Amount *</label>
                        <input
                          type="number"
                          className={`form-control ${formErrors.bookingAmount ? "is-invalid" : ""}`}
                          placeholder="Enter Amount"
                          value={bookingAmount}
                          onChange={(e) => setBookingAmount(e.target.value)}
                        />
                        {formErrors.bookingAmount && (
                          <div className="invalid-feedback">{formErrors.bookingAmount}</div>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Payment Method *</label>
                        <select
                          className={`form-select radius-8 ${formErrors.paymentMethod ? "is-invalid" : ""}`}
                          value={paymentMethod}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                        >
                          <option value="Cash">Cash</option>
                          <option value="UPI">UPI</option>
                        </select>
                        {formErrors.paymentMethod && (
                          <div className="invalid-feedback">{formErrors.paymentMethod}</div>
                        )}
                      </div>
                      {paymentMethod === "UPI" && (
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Payment Screenshot *</label>
                          <input
                            type="file"
                            accept="image/*"
                            className={`form-control ${formErrors.screenshot ? "is-invalid" : ""}`}
                            onChange={handleScreenShotUpload}
                          />
                          {screenshot && (
                            <div className="mt-8">
                              <img
                                src={getImageUrl(screenshot)}
                                alt="Preview"
                                style={{ width: "100px", height: "100px", objectFit: "cover" }}
                                className="radius-8 border shadow-sm"
                              />
                            </div>
                          )}
                          {formErrors.screenshot && (
                            <div className="text-danger text-sm mt-1">{formErrors.screenshot}</div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
              {activeTab === "booked" ? (
                <div className="col-12">
                  <label className="form-label fw-bold">Remark {paymentStatus === "Paid" && <span className="text-danger">*</span>}</label>
                  <textarea
                    className={`form-control ${formErrors.remark ? "is-invalid" : ""}`}
                    rows="2"
                    placeholder="Enter remark..."
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                  />
                  {formErrors.remark && (
                    <div className="invalid-feedback">{formErrors.remark}</div>
                  )}
                </div>
              ) : (
                <div className="col-12">
                  <label className="form-label fw-bold">Remark (Optional)</label>
                  <textarea
                    name="remark"
                    className="form-control"
                    rows="2"
                    placeholder="Enter any remarks..."
                    value={newVisitor.remark}
                    onChange={handleVisitorChange}
                  />
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              style={{ backgroundColor: "#ed1c24", border: "none" }}
            >
              {isEditMode ? "Update Visitor" : activeTab === "enquiry" ? "Save Visitor Enquiry" : "Save Visitor Booking"}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Booking Modal */}
      <Modal
        show={showBookingModal}
        onHide={() => setShowBookingModal(false)}
        centered
      >
        <Modal.Header
          closeButton
          style={{
            background: "linear-gradient(135deg, #000000, #bb0007, #ff0000)",
          }}
        >
          <Modal.Title className="text-white text-sm">
            Confirm Booking: {selectedVisitor?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-24 text-start">
          <div className="row gy-3">
            <div className="col-6">
              <label className="form-label fw-bold">Booking Date</label>
              <StandardDatePicker
                value={bookingDate}
                minDate="today"
                onChange={(e) => setBookingDate(e.target.value)}
              />
            </div>
            <div className="col-4">
              <label className="form-label fw-bold">Payment Status *</label>
              {selectedVisitor?.expoId?.entryType ? (
                // Auto-filled from expo entryType — read only
                <div
                  className="d-flex align-items-center gap-8 px-12 radius-8 border fw-bold"
                  style={{
                    height: "44px",
                    backgroundColor: "#f3f4f6",
                    borderColor: "#d1d5db",
                    color:
                      paymentStatus === "Free" ? "#2563eb" :
                        paymentStatus === "Paid" ? "#16a34a" : "#d97706",
                    fontSize: "14px",
                    cursor: "not-allowed",
                  }}
                >
                  <span
                    className="badge px-10 py-4 text-white fw-bold"
                    style={{
                      fontSize: "11px",
                      backgroundColor:
                        paymentStatus === "Free" ? "#2563eb" :
                          paymentStatus === "Paid" ? "#16a34a" : "#d97706",
                    }}
                  >
                    {paymentStatus.toUpperCase()}
                  </span>
                </div>
              ) : (
                <select
                  className="form-select"
                  value={paymentStatus}
                  onChange={(e) => {
                    const val = e.target.value;
                    setPaymentStatus(val);
                    if (val === "Free") {
                      setPaymentMethod("Free");
                      setBookingAmount("0");
                    } else if (val === "Pending") {
                      setPaymentMethod("Pending");
                      setBookingAmount("0");
                    } else {
                      setPaymentMethod("Cash");
                      setBookingAmount("");
                    }
                  }}
                >
                  <option value="Free">Free</option>
                  <option value="Paid">Paid</option>
                  <option value="Pending">Pending</option>
                </select>
              )}
            </div>
            {paymentStatus === "Paid" && (
              <>
                <div className="col-6">
                  <label className="form-label fw-bold">Payment Type *</label>
                  <select
                    className="form-select"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  >
                    <option value="Cash">Cash</option>
                    <option value="UPI">UPI</option>
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label fw-bold">Booking Amount *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formatIndianNumber(bookingAmount)}
                    onChange={(e) => {
                      const rawVal = e.target.value.replace(/,/g, "");
                      if (!isNaN(rawVal) || rawVal === "") {
                        setBookingAmount(rawVal);
                      }
                    }}
                  />
                </div>
              </>
            )}
            {paymentStatus === "Paid" && paymentMethod === "UPI" && (
              <div className="col-12">
                <label className="form-label fw-bold">Upload Screenshot *</label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={handleScreenShotUpload}
                />
                {screenshot && (
                  <div className="mt-8">
                    <img
                      src={screenshot}
                      alt="Preview"
                      className="radius-8 border"
                      style={{ maxHeight: '100px' }}
                    />
                  </div>
                )}
              </div>
            )}
            <div className="col-12">
              <label className="form-label fw-bold">
                Booking Remark {paymentStatus === "Paid" && <span className="text-danger">*</span>}
              </label>
              <textarea
                className="form-control"
                placeholder="Enter remark for booking..."
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowBookingModal(false)}
          >
            Cancel
          </Button>
          <Button
            style={{ backgroundColor: "#ed1c24", border: "none" }}
            onClick={handleBookingConfirm}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default VisitorReportsLayer;
